import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { GitFork, Crown, User, Users, CheckCircle2 } from "lucide-react";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import PageLoader from "../../helpers/PageLoader";
import PageHeader from "../ui/PageHeader";

export default function KRAReviewWorkflow() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint =
        SummaryApi.getAllKRAReviewWorkflows?.url ||
        "/api/hr/performance/workflows/view-all";
      const res = await api.get(endpoint);
      if (res.data.success) {
        setWorkflows(res.data.data || []);
      }
    } catch {
      toast.error("Failed to fetch KRA review workflows.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  // Repeating color sequence for dynamic workflow visualization steps
  const STAGE_THEMES = [
    {
      block: "bg-[#4A90E2]",
      text: "text-[#4A90E2]",
      pill: "bg-blue-50 text-blue-700 border-blue-100",
      ring: "ring-[#4A90E2]",
      arrow: "#4A90E2",
    },
    {
      block: "bg-[#7CB342]",
      text: "text-[#7CB342]",
      pill: "bg-lime-50 text-lime-800 border-lime-100",
      ring: "ring-[#7CB342]",
      arrow: "#7CB342",
    },
    {
      block: "bg-[#F4711F]",
      text: "text-[#F4711F]",
      pill: "bg-orange-50 text-orange-800 border-orange-100",
      ring: "ring-[#F4711F]",
      arrow: "#F4711F",
    },
    {
      block: "bg-[#F2B705]",
      text: "text-[#F2B705]",
      pill: "bg-amber-50 text-amber-800 border-amber-100",
      ring: "ring-[#F2B705]",
      arrow: "#F2B705",
    },
  ];

  const getTheme = (index) => STAGE_THEMES[index % STAGE_THEMES.length];

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6">
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <PageHeader
        title="Review Workflows"
        description="Configure and visualize performance appraisal pipeline stages for your organization."
        isSticky={true}
        topOffset="top-[51px]"
      />

      {workflows.length === 0 ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-xl p-8 text-center animate-in fade-in duration-500">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6 text-primary ring-8 ring-red-50/50">
            <GitFork className="text-primary" size={32} />
          </div>
          <h3 className="text-lg font-bold text-gray-900">
            No Review Workflows Configured
          </h3>
          <p className="text-gray-400 text-xs mt-1">
            Please configure appraisal stages to visualize performance
            timelines.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {workflows.map((workflow) => (
            <div
              key={workflow._id}
              className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-[0_10px_35px_rgba(0,0,0,0.03)] hover:shadow-md transition-shadow duration-300 space-y-2"
            >
              {/* Card Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-50 pb-4 text-left">
                <div className="space-y-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight leading-snug">
                    {workflow.name}
                  </h3>
                  <p className="text-xs text-gray-400 font-medium">
                    {workflow.description ||
                      "Appraisal process stage timeline."}
                  </p>
                </div>

                <span
                  className={`self-start sm:self-center shrink-0 px-3.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                    workflow.isActive
                      ? "bg-green-50 text-green-700 border-green-200"
                      : "bg-gray-50 text-gray-500 border-gray-200"
                  }`}
                >
                  {workflow.isActive ? "Active Workflow" : "Inactive"}
                </span>
              </div>

              {/* Pipeline Grid wrapper */}
              <div className="overflow-x-auto no-scrollbar">
                <div className="flex items-start gap-0 min-w-max px-2 sm:px-6 pt-10 pb-4">
                  {workflow.stages?.map((stage, idx) => {
                    const theme = getTheme(idx);
                    const isLast = idx === workflow.stages.length - 1;
                    const isUpArc = idx % 2 === 0;
                    const evaluator = stage.specificEvaluatorId;
                    const initials = evaluator
                      ? `${evaluator.firstName?.[0] || ""}${
                          evaluator.lastName?.[0] || ""
                        }`
                      : "";

                    return (
                      <React.Fragment key={stage.stageKey || idx}>
                        {/* Step Card column */}
                        <div className="flex flex-col items-center w-[168px] sm:w-[190px] shrink-0">
                          {/* Tilted numbered block */}
                          <div
                            className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex flex-col items-center justify-center text-white shadow-lg transform rotate-[12deg] hover:rotate-0 transition-transform duration-300 ${theme.block}`}
                          >
                            <span className="text-xl sm:text-2xl font-bold leading-none">
                              {String(idx + 1).padStart(2, "0")}
                            </span>
                            <span className="text-[8px] sm:text-[9px] uppercase font-bold tracking-widest mt-1 opacity-90">
                              Step
                            </span>
                          </div>

                          {/* Connector stem */}
                          <div className="w-px h-4 bg-gray-200 mt-3" />

                          {/* Info card block */}
                          <div className="w-full bg-white border border-gray-200 rounded-2xl p-4 text-center shadow-[0_4px_14px_rgba(0,0,0,0.04)] space-y-2.5 min-h-[176px] flex flex-col justify-between">
                            <div className="space-y-1.5">
                              <p className="text-xs sm:text-[13px] font-bold text-gray-900 leading-tight">
                                {stage.stageName}
                              </p>
                              <span
                                className={`inline-block text-[8px] font-bold uppercase px-2 py-0.5 rounded border tracking-wider ${theme.pill}`}
                              >
                                {stage.evaluatorType?.replace(/_/g, " ")}
                              </span>
                            </div>

                            {/* Evaluator Avatar representation */}
                            <div className="flex justify-center">
                              {evaluator ? (
                                evaluator.profilePicture ? (
                                  <img
                                    src={evaluator.profilePicture}
                                    alt={`${evaluator.firstName} ${evaluator.lastName}`}
                                    className={`w-10 h-10 rounded-full object-cover ring-2 ${theme.ring} shrink-0`}
                                  />
                                ) : (
                                  <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${theme.block}`}
                                  >
                                    {initials || <User size={16} />}
                                  </div>
                                )
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                                  {stage.evaluatorType === "SELF" ? (
                                    <User size={18} />
                                  ) : (
                                    <Users size={18} />
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Evaluator identity details */}
                            {evaluator ? (
                              <div className="text-[10px] space-y-0.5">
                                <div className="flex items-center justify-center gap-1 font-bold text-gray-800">
                                  {evaluator.role === "owner" && (
                                    <Crown className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                  )}
                                  <span className="truncate max-w-[120px]">
                                    {evaluator.firstName} {evaluator.lastName}
                                  </span>
                                </div>
                                <p className="text-[8px] text-gray-400 truncate px-1">
                                  {evaluator.email}
                                </p>
                              </div>
                            ) : (
                              <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wide">
                                {stage.evaluatorType === "SELF"
                                  ? "Self Evaluation"
                                  : stage.evaluatorType === "REPORTING_MANAGER"
                                  ? "Reporting Manager"
                                  : stage.evaluatorType === "SKIP_LEVEL_MANAGER"
                                  ? "Skip-Level Manager"
                                  : stage.evaluatorType === "FUNCTIONAL_HEAD"
                                  ? "Functional Head"
                                  : "Dynamic Evaluator"}
                              </p>
                            )}
                          </div>

                          {/* Connector dot */}
                          <div className="w-2 h-2 rounded-full bg-gray-200 mt-3" />
                        </div>

                        {/* Curved connector arrow between active stages */}
                        {!isLast ? (
                          <div className="flex items-start justify-center w-10 sm:w-14 h-16 sm:h-20 shrink-0 -mx-1 mt-6 sm:mt-8">
                            <svg
                              className="w-10 sm:w-14 h-14 sm:h-16"
                              viewBox="0 0 64 64"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              {isUpArc ? (
                                <>
                                  <path
                                    d="M 2 34 Q 32 -6 62 26"
                                    stroke={theme.arrow}
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                  />
                                  <path
                                    d="M 51 17 L 62 26 L 53 32"
                                    stroke={theme.arrow}
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </>
                              ) : (
                                <>
                                  <path
                                    d="M 2 30 Q 32 66 62 34"
                                    stroke={theme.arrow}
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                  />
                                  <path
                                    d="M 51 42 L 62 34 L 54 28"
                                    stroke={theme.arrow}
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </>
                              )}
                            </svg>
                          </div>
                        ) : (
                          // ✅ Added: Final Appraisal Completed emerald target node (athuku apro aal ila maari)
                          <React.Fragment>
                            {/* Final Emerald connecting arrow segment */}
                            <div className="flex items-start justify-center w-10 sm:w-14 h-16 sm:h-20 shrink-0 -mx-1 mt-6 sm:mt-8">
                              <svg
                                className="w-10 sm:w-14 h-14 sm:h-16"
                                viewBox="0 0 64 64"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                {isUpArc ? (
                                  <>
                                    <path
                                      d="M 2 34 Q 32 -6 62 26"
                                      stroke="#10B981"
                                      strokeWidth="3.5"
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d="M 51 17 L 62 26 L 53 32"
                                      stroke="#10B981"
                                      strokeWidth="3.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </>
                                ) : (
                                  <>
                                    <path
                                      d="M 2 30 Q 32 66 62 34"
                                      stroke="#10B981"
                                      strokeWidth="3.5"
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d="M 51 42 L 62 34 L 54 28"
                                      stroke="#10B981"
                                      strokeWidth="3.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </>
                                )}
                              </svg>
                            </div>

                            {/* Completed pipeline target node */}
                            <div className="flex flex-col items-center w-[120px] sm:w-[140px] shrink-0">
                              <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full flex flex-col items-center justify-center text-white shadow-lg bg-gradient-to-r from-emerald-500 to-green-600 ring-4 ring-green-100">
                                <CheckCircle2 className="w-8 h-8" />
                              </div>
                              <div className="w-px h-4 bg-gray-200 mt-3" />
                              <div className="w-full bg-green-50/50 border border-green-200 rounded-2xl p-3 text-center shadow-[0_4px_14px_rgba(16,185,129,0.08)] space-y-1">
                                <p className="text-xs font-bold text-green-800 leading-none">Completed</p>
                                <p className="text-[9px] text-green-600 font-bold uppercase tracking-wider leading-none">Appraisal Locked</p>
                              </div>
                              <div className="w-2 h-2 rounded-full bg-green-500 mt-3" />
                            </div>
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}